import { ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useVerticalSwipe } from '@/hooks/useVerticalSwipe';

interface ModalDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
}

export const ModalDrawer = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
}: ModalDrawerProps) => {
  const { handleTouchStart, handleTouchMove, handleTouchEnd, dragY } = useVerticalSwipe({
    onSwipeDown: () => onOpenChange(false),
    threshold: 80
  });

  const handleClose = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className={cn(
          "h-[90vh] rounded-t-xl border-0 p-0 flex flex-col bg-background/95 backdrop-blur-sm gap-0 [&>button]:top-6 [&>button]:right-6 [&>button]:focus:outline-none",
          className
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{
          transform: `translateY(${Math.min(dragY * 0.8, 100)}px)`,
          transition: dragY === 0 ? 'transform 0.3s ease-out' : 'none'
        }}
      >
        {(title || description) && (
          <SheetHeader 
            className="px-6 py-4 border-b bg-background/95 backdrop-blur-sm rounded-t-xl relative cursor-grab active:cursor-grabbing"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Drag indicator */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-muted-foreground/30 rounded-full" />
            
            {title && <SheetTitle className="mt-2">{title}</SheetTitle>}
            
          </SheetHeader>
        )}
        <div className="flex-1 flex flex-col overflow-hidden">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
};